import {Component, OnInit, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {NavigationService} from '../../../Services/navigation.service';
import {ResponseModel} from '../../../DataModels/response-model';

@Component({
  selector: 'app-department-form',
  templateUrl: './department-form.component.html',
  styleUrls: ['./department-form.component.css'],
  standalone: false
})
export class DepartmentFormComponent implements OnInit {
  DepartmentForm: UntypedFormGroup;
  vm: any = {
    title: 'Department Form',
    item: {},
    buttonsDisabled: false,
    Preferences: [],
    mainListPage: '/payroll/department-list',
    formInitiated: false,
    defaultItem: [],
    accounts: [],
  };

  submitted = false;
  departmentId = '';
  isSaveNew = false;
  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: StorageService,
    private tableDataService: DataService,
    private notification: BsToastService,
    private titleService: Title,
    private formBuilder: UntypedFormBuilder,
    private nav: NavigationService,
    private destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.vm.title);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.formInit();
    this.loadLookups();
    this.dataInit();
  }

  loadLookups() {
    this.tableDataService.getApiListData('account', '?pageNumber=0&pageSize=999999&state=active').subscribe({
      next: (result: ResponseModel) => {
        this.vm.accounts = result.Data;
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  dataInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.departmentId = params.id;
      if (this.departmentId !== undefined) {
        this.tableDataService.getData('pr_department', this.departmentId).subscribe({
          next: (result: any) => {
            this.vm.item = result;
            this.vm.formInitiated = true;
            this.setValues();
            this.vm.defaultItem = result;
          },
          error: (err) => {
            console.error(err);
            this.notification.showErrorToast($localize`:Error loading department@@errorLoadingDepartment:Error loading department`);
          },
        });
      }
    });
  }

  get f() {
    return this.DepartmentForm.controls;
  }

  formInit() {
    this.DepartmentForm = this.formBuilder.group({
      item_name: new UntypedFormControl('', Validators.required),
      item_cost_center_gl_account_id: new UntypedFormControl(''),
      item_active: new UntypedFormControl(true),
    });
  }

  setValues() {
    this.DepartmentForm.controls.item_name.setValue(this.vm.item.name);
    this.DepartmentForm.controls.item_cost_center_gl_account_id.setValue(this.vm.item.cost_center_gl_account_id);
    this.DepartmentForm.controls.item_active.setValue(this.vm.item.active);
  }

  setupValuesForSave() {
    this.vm.item.name = this.DepartmentForm.controls.item_name.value;
    this.vm.item.cost_center_gl_account_id = this.DepartmentForm.controls.item_cost_center_gl_account_id.value || null;
    this.vm.item.active = this.DepartmentForm.controls.item_active.value;
  }

  saveClose() {
    this.onSave(true);
  }

  saveNew() {
    this.isSaveNew = true;
    this.onSave(false);
  }

  resetForm() {
    this.resetToDefaults();
  }

  onClose() {
    this.nav.backOr(this.vm.mainListPage);
  }

  deleteItem() {
    if (!this.vm.item.id) {
      return false;
    }
    this.vm.buttonsDisabled = true;
    this.tableDataService.deleteDataById('pr_department', this.vm.item.id).subscribe({
      next: () => {
        this.notification.showSuccessToast($localize`:Deleted@@deleted:Deleted`);
        this.onClose();
      },
      error: (err) => {
        this.vm.buttonsDisabled = false;
        console.error(err);
        this.notification.showErrorToast($localize`:Error deleting department@@errorDeletingDepartment:Error deleting department`);
      },
    });
    return true;
  }

  private resetToDefaults(): void {
    this.DepartmentForm.reset({
      item_name: '',
      item_cost_center_gl_account_id: '',
      item_active: true,
    });
    this.vm.item = {};
    this.submitted = false;
    this.vm.buttonsDisabled = false;
    this.departmentId = '';
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    this.vm.buttonsDisabled = true;

    if (this.DepartmentForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors!@@formerrors:Form Errors!`);
      this.vm.buttonsDisabled = false;
      return false;
    }

    this.setupValuesForSave();

    if (!this.vm.item.id) {
      this.tableDataService.post('pr_department', this.vm.item).subscribe({
        next: (item: any) => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          if (saveClose) {
            this.onClose();
          } else if (this.isSaveNew) {
            this.resetToDefaults();
            this.isSaveNew = false;
          }
        },
        error: (error: any) => {
          this.vm.buttonsDisabled = false;
          console.error(error);
        },
        complete: () => {
          this.vm.buttonsDisabled = false;
          this.submitted = false;
        },
      });
    } else {
      this.tableDataService.patch('pr_department', this.vm.item.id, this.vm.item)
        .then(() => {
          this.notification.showSuccessToast($localize`:Saved@@saved:Saved`);
          if (saveClose) {
            this.onClose();
          } else if (this.isSaveNew) {
            this.resetToDefaults();
            this.isSaveNew = false;
          }
        })
        .catch((error: any) => {
          this.vm.buttonsDisabled = false;
          console.error(error);
        })
        .finally(() => {
          this.vm.buttonsDisabled = false;
          this.submitted = false;
        });
    }
    return true;
  }
}
